import { type ApiClient } from "@anna-money/anna-web-lib/api";

import {
  DirectorCHPersonalCodeUpdateData,
  type DirectorCreateData,
  DirectorData,
  DirectorDriversLicenceStateUpdateData,
  DirectorListData,
  type DirectorPreviousAddressUpdateData,
  type DirectorSharesUpdateData,
  DirectorSignificantUpdateData,
  type DirectorUpdateData,
  type MainDirectorCreateData,
} from "services/director/directorTypes";
import { DirectorVerificationStatusData } from "services/directorCodeModal/directorCodeModalTypes";

export class DirectorClient {
  private readonly _apiClient: ApiClient;

  constructor(apiClient: ApiClient) {
    this._apiClient = apiClient;
  }

  async getDirectorList(): Promise<DirectorData[]> {
    const response = await this._apiClient.get("api/formations/v2/directors");
    return response.getData(DirectorListData).directors;
  }

  async createDirector(data: MainDirectorCreateData | DirectorCreateData): Promise<DirectorData> {
    const response = await this._apiClient.post("api/formations/v2/directors", data);

    return response.getData(DirectorData);
  }

  async updateDirector(
    directorId: string,
    data:
      | DirectorUpdateData
      | DirectorSharesUpdateData
      | DirectorSignificantUpdateData
      | DirectorDriversLicenceStateUpdateData
      | DirectorCHPersonalCodeUpdateData
      | DirectorPreviousAddressUpdateData,
  ): Promise<DirectorData> {
    const response = await this._apiClient.post(`api/formations/directors/${directorId}`, data);

    return response.getData(DirectorData);
  }

  async deleteDirector(directorId: string): Promise<void> {
    await this._apiClient.delete(`api/formations/directors/${directorId}`);
  }

  async submitACSPVerification(directorId: string): Promise<void> {
    await this._apiClient.post(`api/formations/directors/${directorId}/acsp`, {});
  }

  async startSecondaryDirectorVerification(): Promise<void> {
    await this._apiClient.post(`api/formations/directors/start-idv-for-secondary-directors`, {});
  }

  async requestDirectorVerification(directorId: string): Promise<void> {
    await this._apiClient.post(`api/formations/directors/${directorId}/idv`, {});
  }

  /** Wipes a finished-but-wrong ID check and sends a fresh verification link */
  async resetDirectorVerification(directorId: string): Promise<void> {
    await this._apiClient.post(`api/formations/directors/${directorId}/reset-idv`, {});
  }

  async getDirectorVerificationStatus(directorId: string): Promise<DirectorVerificationStatusData> {
    const response = await this._apiClient.get(`api/formations/directors/${directorId}/idv`);
    return response.getData(DirectorVerificationStatusData);
  }
}
